'use client'

import type { DragEvent } from 'react'
import type { Task, TaskStatus } from '@/lib/types'
import type { RiskKind } from '@/lib/dev-sprint'
import { StatusPill, PriorityDot, PointsChip, RiskChip } from './ui'

interface Props {
  task: Task
  projectName: string
  projectColor: string
  status: TaskStatus
  risks: RiskKind[]
  onClick: () => void
  showStatus?: boolean
  draggable?: boolean
  onDragStart?: (e: DragEvent) => void
  onDragEnd?: (e: DragEvent) => void
}

export function TaskCard({
  task, projectName, projectColor, status, risks, onClick, showStatus = true, draggable, onDragStart, onDragEnd,
}: Props) {
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick() }}
      className={`group rounded-xl border border-[#E7E5DF] bg-white p-2.5 text-left shadow-sm hover:border-[#D4A843]/60 hover:shadow transition-all ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: projectColor }} />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#AEADA8] truncate">{projectName}</span>
      </div>
      <p className="text-[13px] font-semibold text-[#1A1916] leading-snug line-clamp-2">{task.title}</p>
      <div className="mt-2 flex items-center flex-wrap gap-1.5">
        <PointsChip points={task.points} />
        <PriorityDot priority={task.priority} />
        {showStatus && <StatusPill status={status} />}
      </div>
      {risks.length > 0 && (
        <div className="mt-1.5 flex items-center flex-wrap gap-1">
          {risks.map((r) => <RiskChip key={r} kind={r} />)}
        </div>
      )}
    </div>
  )
}
